// Reusable page container component

import { Box, Container, Heading,  Text, useColorModeValue, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { Breadcrumbs, BreadcrumbItem } from '../shared/Breadcrumbs';
import { Header } from './Header';

interface PageContainerProps {
  title?: string;
  description?: string;
  header?: boolean;
  action?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Container width. Defaults to the standard '6xl'; widen for dense side-by-side layouts. */
  maxW?: string;
  /** Header/action alignment. Defaults to 'center'; 'left' suits dense layouts. */
  headerAlign?: 'center' | 'left';
  /**
   * Where the action sits. 'below' (default) gives it its own row under the title.
   * 'inline' puts it opposite the title on the same row, for dense pages where a
   * second header row wastes vertical space. Only meaningful with headerAlign="left".
   */
  actionPlacement?: 'below' | 'inline';
  children: ReactNode;
}

/**
 * Consistent page wrapper with common layout patterns
 */
export function PageContainer({
  title,
  description,
  header,
  action,
  breadcrumbs,
  maxW = '6xl',
  headerAlign = 'center',
  actionPlacement = 'below',
  children,
}: PageContainerProps) {
  const bgGradient = useColorModeValue(
    'linear(to-br, brand.50, white, brand.50)',
    'linear(to-br, gray.900, gray.800, gray.900)'
  );
  const isLeft = headerAlign === 'left';
  const isInline = isLeft && actionPlacement === 'inline';
  return (
    <Box p={6} bg="bg-canvas" minH="100vh" bgGradient={bgGradient}>
      {header && <Header />}
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      <Box py={isLeft ? 6 : 12}>
        <Container maxW={maxW}>
          <VStack spacing={isLeft ? 5 : 8} align="stretch">
            {isInline ? (
              (title || action) && (
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={4}>
                  {title && (
                    <VStack spacing={2} align="flex-start">
                      <Heading size="lg" color="text-primary">{title}</Heading>
                      <Text color="text-secondary">{description}</Text>
                    </VStack>
                  )}
                  {action}
                </Box>
              )
            ) : (
              <>
                {title && (
                  <VStack
                    spacing={2}
                    textAlign={headerAlign}
                    align={isLeft ? 'flex-start' : 'center'}
                    mb={isLeft ? 0 : 3}
                  >
                    <Heading size={isLeft ? 'lg' : 'xl'} color="text-primary">{title}</Heading>
                    <Text color="text-secondary">
                        {description}
                    </Text>
                  </VStack>
                )}
                {action && (
                  <Box display="flex" justifyContent={isLeft ? 'flex-start' : 'center'}>
                    {action}
                  </Box>
                )}
              </>
            )}
            {children}
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}
